import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/products.ts'
import { productDefinition } from '#app/routes/mocra+/_form+/components/products/definition.ts'
import { ProductEditOverlay } from '#app/routes/mocra+/_form+/components/products/edit.tsx'
import {
	ProductEntrySchema,
	ProductDataSourceEnum,
	type ProductEntry,
	type ProductDataSourceValue,
} from '#app/routes/mocra+/_form+/components/products/schema.ts'
import { type ProductDraft } from '#app/routes/mocra+/_form+/components/products/types.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useEntityCollection } from '#app/routes/mocra+/_form+/hooks/use-entity-collection.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import {
	PRODUCT_CATEGORY_OPTIONS,
	PRODUCT_DATA_SOURCE_OPTIONS,
	PRODUCT_STATUS_OPTIONS,
	PROFESSIONAL_USE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'
import { PortfolioOverviewSection } from '#app/routes/mocra+/_form+/products/portfolio-overview-section.tsx'
import { ProductRosterSection } from '#app/routes/mocra+/_form+/products/product-roster-section.tsx'
import { ProductSystemSection } from '#app/routes/mocra+/_form+/products/product-system-section.tsx'
import { createRouteSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

export const meta: Route.MetaFunction = () => [{ title: 'MoCRA — Products' }]

const SECTION_KEY = 'products'
const STEP_ID = 'products'
const PRODUCTS_RESOURCES = createRouteSectionResources({
	route: '/mocra/products',
})

const ProductsSchema = z
	.object({
		activeSkuCount: z
			.string({
				required_error: 'Tell us how many cosmetic SKUs you sell in the U.S.',
			})
			.min(1, 'Tell us how many cosmetic SKUs you sell in the U.S.'),
		products: z.array(ProductEntrySchema, {
			required_error: 'Add at least one product.',
		}),
		productDataSource: ProductDataSourceEnum,
		productDataSourceOther: z
			.string()
			.max(200, 'Keep the custom system description under 200 characters.')
			.optional(),
	})
	.superRefine((data, ctx) => {
		if (data.productDataSource === 'other') {
			if (!data.productDataSourceOther?.trim()) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Describe the system you use to manage product data.',
					path: ['productDataSourceOther'],
				})
			}
		}
	})

export type ProductsFormValues = z.infer<typeof ProductsSchema>
export type ProductFormEntry = ProductEntry

const DEFAULT_VALUES: ProductsFormValues = {
	activeSkuCount: '',
	products: [],
	productDataSource: 'unspecified',
	productDataSourceOther: '',
}

const PRODUCT_DATA_SOURCE_VALUE_SET = new Set<ProductDataSourceValue>(
	PRODUCT_DATA_SOURCE_OPTIONS.map(
		(option): ProductDataSourceValue => option.value,
	),
)

export default function Products() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [editingIndex, setEditingIndex] = useState<number | null>(null)
	const form = useSectionForm({
		section: SECTION_KEY,
		schema: ProductsSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const {
		values,
		setValues,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = form

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizeProductsDraft(values)
		if (normalized && !productsValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
		if (!normalized && !productsValuesEqual(DEFAULT_VALUES, values)) {
			setValues(DEFAULT_VALUES)
		}
	}, [setValues, values])

	const skuCount = values.activeSkuCount ?? ''
	const productsCollection = useEntityCollection<
		ProductsFormValues,
		ProductEntry,
		ProductDraft
	>({
		form,
		fieldPath: 'products',
		definition: productDefinition,
		getFieldErrorPaths: getProductFieldErrorPaths,
	})
	const productItems = useMemo(
		() =>
			productsCollection.items.map(({ draft, errors }) => ({
				draft,
				errors,
			})),
		[productsCollection.items],
	)
	const productWarnings =
		productsCollection.items.length === 0 ? ['Add at least one product.'] : []

	const productDataSourceValue = isProductDataSourceValue(
		values.productDataSource,
	)
		? values.productDataSource
		: 'unspecified'
	const productDataSourceOtherValue = values.productDataSourceOther ?? ''
	const showProductDataSourceOther = productDataSourceValue === 'other'

	const skuErrors = getFieldErrors('activeSkuCount')
	const productDataSourceErrors = getFieldErrors('productDataSource')
	const productDataSourceOtherErrors = getFieldErrors('productDataSourceOther')

	const editingDraft =
		editingIndex !== null && productsCollection.items[editingIndex]
			? productsCollection.items[editingIndex]?.draft
			: productsCollection.createDraft()

	const resources = PRODUCTS_RESOURCES

	const handleSkuCountChange = (value: string) => {
		setFieldValue('activeSkuCount', value)
	}

	const handleAddProduct = (draft: ProductDraft) => {
		productsCollection.append(draft)
	}

	const handleEditProduct = (draft: ProductDraft) => {
		if (editingIndex === null) return
		productsCollection.replace(editingIndex, draft)
		setEditingIndex(null)
	}

	const handleRemoveProduct = (index: number) => {
		productsCollection.remove(index)
		setEditingIndex(null)
	}

	const handleProductDataSourceChange = (value: ProductDataSourceValue) => {
		setValues((previous) => ({
			...previous,
			productDataSource: value,
			productDataSourceOther:
				value === 'other' ? (previous.productDataSourceOther ?? '') : '',
		}))
	}

	const handleProductDataSourceOtherChange = (value: string) => {
		setFieldValue('productDataSourceOther', value)
	}

	return (
		<Page
			title="Products"
			description="Capture the product roster that flows into §607 listings, safety dossiers, and recall scripts."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />
				<PortfolioOverviewSection
					resources={resources}
					skuCount={skuCount}
					errors={skuErrors}
					onChange={handleSkuCountChange}
				/>

				<Divider />

				<ProductRosterSection
					resources={resources}
					products={productItems}
					warnings={productWarnings}
					onAdd={handleAddProduct}
					onEdit={(index) => setEditingIndex(index)}
					onRemove={handleRemoveProduct}
				/>
				<ProductEditOverlay
					open={editingIndex !== null}
					onOpenChange={(open) => {
						if (!open) {
							setEditingIndex(null)
						}
					}}
					onSubmit={handleEditProduct}
					initialValue={editingDraft}
					onClosed={() => setEditingIndex(null)}
					onCancel={() => setEditingIndex(null)}
				/>

				<Divider />

				<ProductSystemSection
					resources={resources}
					value={productDataSourceValue}
					errors={productDataSourceErrors}
					showOther={showProductDataSourceOther}
					otherValue={productDataSourceOtherValue}
					otherErrors={productDataSourceOtherErrors}
					onValueChange={handleProductDataSourceChange}
					onOtherChange={handleProductDataSourceOtherChange}
				/>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}
				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

function getProductFieldErrorPaths(index: number): readonly string[] {
	const prefix = `products.${index}`
	return [
		prefix,
		`${prefix}.name`,
		`${prefix}.sku`,
		`${prefix}.category`,
		`${prefix}.categoryOther`,
		`${prefix}.professionalUse`,
		`${prefix}.status`,
	]
}

function isProductDataSourceValue(
	value: unknown,
): value is ProductDataSourceValue {
	if (typeof value !== 'string') return false
	return PRODUCT_DATA_SOURCE_VALUE_SET.has(value as ProductDataSourceValue)
}

function createProduct(): ProductFormEntry {
	return {
		name: '',
		sku: '',
		category: 'leave_on_skincare',
		categoryOther: '',
		professionalUse: 'consumer',
		status: 'listed',
	}
}

function normalizeProductEntry(entry: unknown): ProductFormEntry | null {
	if (!entry || typeof entry !== 'object') return null
	const data = entry as Partial<ProductFormEntry> & Record<string, unknown>
	const base = createProduct()
	const categoryValues = PRODUCT_CATEGORY_OPTIONS.map(
		(option): (typeof PRODUCT_CATEGORY_OPTIONS)[number]['value'] =>
			option.value,
	)
	const statusValues = PRODUCT_STATUS_OPTIONS.map(
		(option): (typeof PRODUCT_STATUS_OPTIONS)[number]['value'] => option.value,
	)
	const professionalValues = PROFESSIONAL_USE_OPTIONS.map(
		(option): (typeof PROFESSIONAL_USE_OPTIONS)[number]['value'] =>
			option.value,
	)

	const category =
		typeof data.category === 'string' && categoryValues.includes(data.category)
			? data.category
			: 'other'
	const normalized: ProductFormEntry = {
		name:
			typeof data.name === 'string' && data.name.trim()
				? data.name.trim()
				: base.name,
		sku:
			typeof data.sku === 'string'
				? data.sku.trim()
				: typeof (data as Record<string, unknown>).sku === 'number'
					? String(data.sku)
					: base.sku,
		category,
		categoryOther:
			category === 'other'
				? typeof data.categoryOther === 'string'
					? data.categoryOther.trim()
					: ''
				: '',
		professionalUse:
			typeof data.professionalUse === 'string' &&
			professionalValues.includes(data.professionalUse)
				? data.professionalUse
				: base.professionalUse,
		status:
			typeof data.status === 'string' && statusValues.includes(data.status)
				? data.status
				: base.status,
	}

	if (!normalized.name) return null
	if (normalized.category === 'other' && !normalized.categoryOther) {
		normalized.categoryOther = 'Unspecified'
	}

	return normalized
}

export function normalizeProductsDraft(
	draft: unknown,
): ProductsFormValues | null {
	if (!draft || typeof draft !== 'object') return null
	const data = draft as Partial<ProductsFormValues> & Record<string, unknown>

	const activeSkuCountValue =
		typeof data.activeSkuCount === 'string'
			? data.activeSkuCount
			: typeof data.activeSkuCount === 'number'
				? String(data.activeSkuCount)
				: ''

	const productCategoriesRaw =
		typeof data.productCategories === 'string'
			? data.productCategories
			: undefined

	const products: ProductFormEntry[] = Array.isArray(data.products)
		? data.products
				.map((entry) => normalizeProductEntry(entry))
				.filter((entry): entry is ProductFormEntry => Boolean(entry))
		: []

	if (
		products.length === 0 &&
		productCategoriesRaw &&
		productCategoriesRaw.trim()
	) {
		products.push({
			...createProduct(),
			name: 'Portfolio mix',
			category: 'other',
			categoryOther: productCategoriesRaw.trim(),
			status: 'listed',
		})
	}

	const plannedLaunchesRaw =
		typeof data.plannedLaunches === 'string' ? data.plannedLaunches : undefined

	let productDataSource: ProductDataSourceValue = 'unspecified'
	let productDataSourceOther = ''
	const rawDataSource =
		typeof data.productDataSource === 'string'
			? data.productDataSource.trim()
			: undefined
	const rawDataSourceOther =
		typeof data.productDataSourceOther === 'string'
			? data.productDataSourceOther.trim()
			: undefined

	const availableDataSourceValues = new Set(
		PRODUCT_DATA_SOURCE_OPTIONS.map((option) => option.value),
	)

	if (
		rawDataSource &&
		availableDataSourceValues.has(rawDataSource as ProductDataSourceValue)
	) {
		productDataSource = rawDataSource as ProductDataSourceValue
		if (productDataSource === 'other') {
			productDataSourceOther = rawDataSourceOther?.trim() ?? ''
			if (!productDataSourceOther && plannedLaunchesRaw) {
				productDataSourceOther = plannedLaunchesRaw.trim()
			}
		} else {
			productDataSourceOther = rawDataSourceOther?.trim() ?? ''
		}
	} else if (rawDataSource) {
		productDataSource = 'other'
		productDataSourceOther = rawDataSourceOther?.trim() || rawDataSource
	} else if (rawDataSourceOther) {
		productDataSource = 'other'
		productDataSourceOther = rawDataSourceOther.trim()
	}

	if (
		productDataSource === 'other' &&
		!productDataSourceOther &&
		plannedLaunchesRaw
	) {
		productDataSourceOther = plannedLaunchesRaw.trim()
	}

	return {
		activeSkuCount: activeSkuCountValue,
		products,
		productDataSource,
		productDataSourceOther,
	}
}

export function productsValuesEqual(
	left: ProductsFormValues,
	right: ProductsFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}
